import React from "react"
import { Alert, Card, Col, Form, Input, Row, Select, Space } from "antd"
import { FileTextOutlined } from "@ant-design/icons"

const { Option } = Select

interface NoteConfigCardProps {
  noteType: 'CREDIT_NOTE' | 'DEBIT_NOTE'
  onNoteTypeChange: (val: 'CREDIT_NOTE' | 'DEBIT_NOTE') => void
  reasonOptions: any[]
  onReasonChange: (value: string) => void
  destinationWatch: string
  isAnnulment: boolean
  isPhysical: boolean
  usesCashSessions: boolean
  hasActiveSession: boolean
  sessionId?: number | null
  cashierName?: string | null
}

export const NoteConfigCard: React.FC<NoteConfigCardProps> = ({
  noteType,
  onNoteTypeChange,
  reasonOptions,
  onReasonChange,
  destinationWatch,
  isAnnulment,
  isPhysical,
  usesCashSessions,
  hasActiveSession,
  sessionId,
  cashierName
}) => {
  return (
    <Card 
      title={
        <Space align="center">
          <FileTextOutlined style={{ color: '#1890ff' }} />
          <span>Configuración de la Nota</span>
        </Space>
      }
      className="card shadow-sm"
      size="small"
      style={{ marginBottom: 16 }}
    >
      <Row gutter={[16, 12]}>
        <Col xs={24} sm={12} md={12} xl={12} xxl={6}>
          <Form.Item label="Tipo de Nota" name="type" initialValue="CREDIT_NOTE">
            <Select onChange={(val: any) => onNoteTypeChange(val)}>
              <Option value="CREDIT_NOTE">Nota de Crédito (Devolución/Descuento)</Option>
              <Option value="DEBIT_NOTE">Nota de Débito (Ajuste a favor / Mayor valor)</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={12} xl={12} xxl={6}>
          <Form.Item label="Motivo" name="reason" rules={[{ required: true, message: 'Seleccione un motivo' }]}>
            <Select 
              placeholder="Seleccione el motivo" 
              onChange={onReasonChange}
              showSearch
              optionFilterProp="children"
            >
              {reasonOptions.map(opt => (
                <Option key={opt.key} value={opt.key}>{opt.string_value}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={12} xl={12} xxl={6}>
          <Form.Item 
            label="Afectación Financiera" 
            name="destination"
            rules={[{ required: true, message: 'Seleccione la afectación financiera' }]}
          >
            <Select placeholder="Seleccione la afectación">
              <Option value="CREDIT">Afectar Cartera / Cuenta por Cobrar</Option>
              <Option value="CASH">Afectar Caja / Efectivo</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={12} xl={12} xxl={6}>
          <Form.Item label="Notas adicionales" name="notes">
            <Input placeholder="Ej: Ajuste por mayor valor acordado / intereses" />
          </Form.Item>
        </Col>
      </Row>

      {isAnnulment && (
        <Alert 
          message="Anulación / Devolución Total de Mercancía" 
          description="Los ítems y cantidades se han cargado automáticamente y no pueden modificarse ni eliminarse para garantizar la correspondencia total con la factura. La totalidad de las unidades vendidas reingresará a la bodega de origen."
          type="warning" 
          showIcon 
          style={{ marginTop: 8 }} 
        />
      )}

      {noteType === 'CREDIT_NOTE' && isPhysical && !isAnnulment && (
        <Alert 
          message="Devolución Parcial de Mercancía" 
          description="Las unidades devueltas reingresarán automáticamente al inventario físico en la bodega de origen de la venta."
          type="info" 
          showIcon 
          style={{ marginTop: 8 }} 
        />
      )}

      {noteType === 'CREDIT_NOTE' && !isPhysical && (
        <Alert 
          message="Ajuste Financiero / Descuento (Sin afectación de inventario)" 
          description="Esta nota no genera reingreso físico de mercancía al almacén ni reduce el saldo de unidades retornables de la factura."
          type="info" 
          showIcon 
          style={{ marginTop: 8 }} 
        />
      )}

      {destinationWatch === 'CASH' && usesCashSessions && !hasActiveSession && (
        <Alert 
          message="Sin Sesión de Caja Activa" 
          description="No se detectó una sesión de caja abierta para tu usuario. La nota se registrará con devolución en efectivo, pero no afectará el arqueo de ninguna caja física."
          type="warning" 
          showIcon 
          style={{ marginTop: 8 }} 
        />
      )}

      {destinationWatch === 'CASH' && usesCashSessions && hasActiveSession && (
        <Alert 
          message={`Sesión de Caja Activa Vinculada (#${sessionId})`} 
          description={`El movimiento en efectivo se aplicará automáticamente a la caja de ${cashierName}.`}
          type="success" 
          showIcon 
          style={{ marginTop: 8 }} 
        />
      )}
    </Card>
  )
}
